<template>
    <div class="mobile-nav">
        <VJoyIconButton
            v-if="mobileMenuHasContent"
            size="small"
            variant="ghost"
            :icon="isMenuOpen ? 'cross' : 'menu'"
            data-testid="burger-menu-cta"
            aria-label="Menu"
            class="button-burger"
            @click="emit('toggle-burger-menu')"
        />

        <a v-if="hasThemeStrategy" :href="logoHref" class="mobile-nav__strategy-logo">
            <MaltImg src="/cms-front/malt-strategy-logo.png" alt="Malt Strategy Logo" height="28" />
        </a>

        <a
            v-else
            :href="logoHref"
            class="mobile-nav__logo"
            :class="{'mobile-nav__logo--square': !isSearchPage && ((searchIsVisible && !loggedIn) || isCompany)}"
        >
            <MaltImg src="/cms-front/maltLogo.png" alt="Malt logo" height="32" />
        </a>

        <div v-if="!loggedIn" class="mobile-nav__controls-wrapper">
            <div data-testid="header-customized-zone">
                <div id="header-customized-zone-search-tablet" />
                <div v-if="isSearchPage && headerSearchSlice" class="mobile-nav__search-banner">
                    <SliceZone :slices="[headerSearchSlice]" :components="components" />
                </div>

                <HeaderSearch
                    v-else-if="!loggedIn || isCompany"
                    v-show="searchIsVisible"
                    :header-search-slice="headerSearchSlice"
                    :search-placeholder-text="navbar?.data.searchPlaceholderText"
                />
            </div>

            <VJoyIconButton
                v-if="isSearchPage || (searchIsVisible && (!loggedIn || isCompany))"
                size="small"
                variant="ghost"
                :icon="isSearchOpen ? 'cross' : 'search'"
                :class="{'mobile-nav__search-btn': true, 'mobile-nav__search-btn--isSearchPage': isSearchPage}"
                data-testid="navbar-mobile-search-button"
                @click="emit('toggle-search-modal')"
            />
            <template v-if="$ff('enable-auth-link')">
                <AuthLink v-if="signupText" class="auth-button sign-up-button" data-testid="navbar-signup-cta" role="signup" :redirect="redirectUrl">
                    {{ signupText }}
                </AuthLink>
                <AuthLink class="auth-button sign-in-button" data-testid="navbar-signin-cta" role="signin" :redirect="signinRedirectUrl">
                    {{ signInText }}
                </AuthLink>
                <AuthLink
                    class="auth-button sign-in-button-icon"
                    data-testid="navbar-signin-cta-xxs"
                    role="signin"
                    :redirect="signinRedirectUrl"
                    :aria-label="signInText"
                >
                    <VJoyIcon name="user-lighter" size="small" />
                </AuthLink>
            </template>
            <template v-else>
                <a v-if="signupText" rel="nofollow" class="sign-up-button" data-testid="navbar-signup-cta" :href="signupUrl">
                    {{ signupText }}
                </a>
                <a rel="nofollow" class="sign-in-button" :href="signinUrl" data-testid="navbar-signin-cta">
                    {{ signInText }}
                </a>
                <VJoyIconButton
                    :href="signinUrl"
                    :title="signInText"
                    size="small"
                    variant="ghost"
                    icon="user-lighter"
                    data-testid="navbar-signin-cta-xxs"
                    class="sign-in-button-icon"
                />
            </template>
            <div class="mobile-nav__controls__locale">
                <LazyLocaleSelection />
            </div>
        </div>
    </div>

    <div id="mobile-menu" :class="{modale: true, open: isMenuOpen}" data-testid="mobile-menu">
        <div v-if="loggedIn">
            <div class="header-user-slice-wrapper">
                <SliceZone :slices="slices.filter(filterFactory('header_user_slice'))" :components="components" />
            </div>
            <SliceZone :slices="slices.filter(filterFactory('user_settings_slice'))" :components="components" />
        </div>
        <template v-else>
            <div>
                <div role="tablist">
                    <SliceZone :slices="slices.filter(filterFactory('megamenu'))" :components="components" :context="{mobile: true}" />
                </div>
                <SliceZone :slices="slices.filter(filterFactory('header_links_slice'))" :components="components" />
            </div>
            <MobileMenuFooter :logged-in="loggedIn" :locale="locale" :slices="slices" :country-slice="countrySlice" />
        </template>
    </div>
    <div
        v-if="isSearchPage || (searchIsVisible && (!loggedIn || isCompany))"
        id="mobile-search"
        :class="{modale: true, open: isSearchOpen}"
        hide-backdrop
        hide-header
        hide-footer
    >
        <div class="mobile-search">
            <SliceZone :slices="slices.filter(filterFactory('header_search_slice'))" :components="components" />
        </div>
        <MobileMenuFooter :logged-in="loggedIn" :locale="locale" :slices="slices" :country-slice="countrySlice" />
    </div>
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import HeaderSearch from '@navbar-unlogged/components/HeaderSearch.vue';
    import {useNavbarUser} from '@navbar-unlogged/composables';
    import {usePageSource} from '@navbar-unlogged/composables/usePageSource';
    import {components} from '@navbar-unlogged/slices';
    import type {PrismicDocument, SharedSlice} from '@prismicio/types';
    import {SliceZone} from '@prismicio/vue';
    import type {CountrySliceSlice, HeaderSearchSliceSlice} from '@prismicio-types';
    import {useRequestURL} from '#app';
    import {AuthLink, LazyLocaleSelection, MaltImg} from '#components';
    import {useLocale, useSigninRedirectUrl} from '#imports';
    import {VJoyIcon, VJoyIconButton} from '@maltjoy/core-vue';
    import MobileMenuFooter from './MobileMenuFooter.vue';

    const props = defineProps<{
        searchIsVisible?: boolean;
        isSearchPage: boolean;
        slices: SharedSlice[];
        headerSearchSlice: HeaderSearchSliceSlice;
        countrySlice: CountrySliceSlice;
        navbar: PrismicDocument | undefined | null;
        isSearchOpen: boolean;
        isMenuOpen: boolean;
        hasThemeStrategy: boolean;
        logoHref: string;
    }>();

    const emit = defineEmits<{
        'toggle-search-modal': [];
        'toggle-burger-menu': [];
    }>();

    // Composables
    const {loggedIn, isCompany} = useNavbarUser();
    const {locale} = useLocale();
    const {pageSource} = usePageSource();
    const url = useRequestURL();
    const {signinRedirectUrl, signinRedirectQuery} = useSigninRedirectUrl();

    // Computed values
    const mobileMenuHasContent = computed(
        () => props.slices.filter(filterFactory('megamenu')).length || props.slices.filter(filterFactory('header_links_slice')).length,
    );

    const headerUserSlice = computed(() => props.slices.filter(filterFactory('header_user_slice'))[0]);
    const signupText = computed(() => headerUserSlice.value?.primary.signUpText);
    const signInText = computed(() => headerUserSlice.value?.primary.signInText);

    const redirectUrl = computed(() => url.pathname + url.search);
    const maybeRedirectParam = computed(() => `&redirect=${encodeURIComponent(url.pathname + url.search)}`);
    const signinUrl = computed(() => `/signin?pageSource=${pageSource.value}&componentSource=navbar-unlogged${signinRedirectQuery.value}`);
    const signupUrl = computed(() => `/who-are-you?pageSource=${pageSource.value}&componentSource=navbar-unlogged${maybeRedirectParam.value}`);

    // Handlers
    function filterFactory(sliceType: string): (slice: SharedSlice) => boolean {
        return (slice: SharedSlice) => slice.slice_type === sliceType;
    }
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@navbar-unlogged/scss/variables.scss' as vars;

    .mobile-nav {
        display: flex;
        width: 100%;
        align-items: center;
        justify-content: space-between;

        &__controls__locale {
            display: none;

            @include mq.screen_sm(min) {
                display: block;
            }
        }

        &__controls-wrapper {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            width: 100%;
            gap: var(--joy-core-spacing-4);

            .sign-up-button {
                text-decoration: none;
                user-select: none;

                @include vars.button-primary-red;
                @include vars.button-xsmall;

                padding: var(--joy-core-spacing-2) var(--joy-core-spacing-3);

                .theme-strategy & {
                    @include vars.button-primary-blue;
                    @include vars.button-xsmall;

                    @include mq.screen_md(min) {
                        @include vars.button-small;
                    }
                }

                @include mq.screen_md(min) {
                    @include vars.button-small;
                }
            }

            .sign-in-button {
                display: none;
                align-items: flex-end;
                text-decoration: none;

                @include vars.link;

                font-size: var(--joy-font-size-primary-300);

                @include mq.screen_xs(min) {
                    display: flex;
                }
            }

            .sign-in-button-icon {
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                border: none;
                background: transparent;
                cursor: pointer;

                @include mq.screen_xs(min) {
                    display: none;
                }
            }

            .auth-button {
                cursor: pointer;
                user-select: none;
                white-space: nowrap;
            }
        }

        &__logo {
            flex: none;
            display: block;
            width: auto;
            overflow: hidden;
            margin-right: auto;

            img {
                display: block;
                height: 24px;

                @include mq.screen_md(min) {
                    height: 32px;
                }
            }

            &--square {
                width: 21px;

                @include mq.screen_md(min) {
                    width: auto;
                }
            }
        }

        &__strategy-logo {
            flex: none;
            display: block;
            overflow: hidden;
            margin-right: auto;
            width: 21px;
            position: relative;
            top: 2px;

            img {
                display: block;
                height: 24px;

                @include mq.screen_md(min) {
                    height: 28px;
                }
            }

            @include mq.screen_sm(min) {
                width: auto;
            }
        }

        &__search-btn {
            display: flex;

            @include mq.screen_sm(min) {
                display: none !important;
            }

            &--isSearchPage {
                @include mq.screen_sm(min) {
                    display: block !important;
                }
            }
        }

        &__search-banner {
            @include mq.screen_md {
                display: none;
            }
        }
    }

    .button-burger {
        flex: none;
        position: relative;
        top: 2px;

        &:focus {
            border-color: transparent;
        }
    }
</style>
